import { useEffect, useState } from "react";
import { fileObjectUrl } from "@shared/ui/FileImage";

/**
 * Где на картинке украшения лежит аватар.
 *
 * Украшение бывает любой формы: шляпа торчит вверх, тыква — вбок, крылья —
 * в обе стороны. Квадрат в 1,2 раза больше аватара, как в Discord, такое
 * обрезал. Поэтому мера едет в самом файле: `scripts/fit-decoration.py`
 * кладёт в PNG кусок `tEXt` с ключом `airel-avatar` и тремя числами —
 * серединой аватара (доли ширины и высоты картинки) и его поперечником (доля
 * ширины). Сервер хранит файл байт в байт, так что базе и API о мере знать
 * незачем.
 */
export interface DecorationFit {
  /** Середина аватара: доли ширины и высоты картинки. */
  x: number;
  y: number;
  /** Поперечник аватара — доля ширины картинки. */
  size: number;
  /** Высота картинки к её ширине. */
  aspect: number;
}

/** Украшение без меры — прежнее, как в Discord: квадрат в 1,2 раза больше аватара, аватар посередине. */
export const LEGACY_FIT: DecorationFit = { x: 0.5, y: 0.5, size: 1 / 1.2, aspect: 1 };

const KEYWORD = "airel-avatar";
const SIGNATURE = [137, 80, 78, 71, 13, 10, 26, 10];

function ascii(bytes: Uint8Array): string {
  return String.fromCharCode(...bytes);
}

/** Мера из начала PNG. Не PNG, без меры или с мерой не в своём уме — прежний квадрат. */
export function readFit(bytes: Uint8Array): DecorationFit {
  if (bytes.length < 33 || SIGNATURE.some((byte, i) => bytes[i] !== byte)) return LEGACY_FIT;

  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  const width = view.getUint32(16);
  const height = view.getUint32(20);
  if (!width || !height) return LEGACY_FIT;

  // Куски — длина, тип, данные, сумма. Мера лежит до картинки: дальше IDAT не читаем.
  for (let at = 8; at + 8 <= bytes.length; ) {
    const length = view.getUint32(at);
    const type = ascii(bytes.subarray(at + 4, at + 8));
    if (type === "IDAT" || type === "IEND") break;

    if (type === "tEXt") {
      const data = bytes.subarray(at + 8, Math.min(bytes.length, at + 8 + length));
      const zero = data.indexOf(0);
      if (zero > 0 && ascii(data.subarray(0, zero)) === KEYWORD) {
        const [x, y, size] = ascii(data.subarray(zero + 1)).trim().split(/\s+/).map(Number);
        const sane = [x, y, size].every(Number.isFinite) && size > 0 && size <= 1 && x >= 0 && x <= 1 && y >= 0 && y <= 1;
        return sane ? { x, y, size, aspect: height / width } : LEGACY_FIT;
      }
    }

    at += 12 + length;
  }

  return LEGACY_FIT;
}

/*
 * Мера читается из уже скачанного `FileImage` файла — второго запроса нет.
 * Хватает начала: скрипт кладёт меру перед картинкой.
 */
const known = new Map<string, DecorationFit>();
const pending = new Map<string, Promise<DecorationFit>>();

function load(id: string): Promise<DecorationFit> {
  const started = pending.get(id);
  if (started) return started;

  const loading = fileObjectUrl(id)
    .then((url) => fetch(url))
    .then((response) => response.blob())
    .then((blob) => blob.slice(0, 64 * 1024).arrayBuffer())
    .then((buffer) => {
      const fit = readFit(new Uint8Array(buffer));
      known.set(id, fit);
      return fit;
    });

  // Неудачу не запоминаем: следующий показ попробует снова.
  loading.catch(() => pending.delete(id));
  pending.set(id, loading);
  return loading;
}

/** Мера украшения; пока файл не прочитан — `null`, и украшение не рисуется, чтобы не прыгать. */
export function useDecorationFit(id: string | null | undefined): DecorationFit | null {
  const [fit, setFit] = useState<DecorationFit | null>(() => (id ? (known.get(id) ?? null) : null));

  useEffect(() => {
    if (!id) {
      setFit(null);
      return;
    }

    const ready = known.get(id);
    if (ready) {
      setFit(ready);
      return;
    }

    let alive = true;
    setFit(null);
    load(id).then(
      (value) => alive && setFit(value),
      () => undefined,
    );

    return () => {
      alive = false;
    };
  }, [id]);

  return fit;
}

/** Коробка украшения у плашки `size`: где лежит и какого размера, в точках от угла плашки. */
export function decorationBox(fit: DecorationFit, size: number) {
  const width = size / fit.size;
  const height = width * fit.aspect;
  return { left: size / 2 - fit.x * width, top: size / 2 - fit.y * height, width, height };
}
